import { gql, useLazyQuery } from "@apollo/client";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
// @mui material components
import {
    Modal,
    Fade,
    Card,
    Grid,
    IconButton,
    Tooltip,
    Icon,
    Typography,
    Box,
    Paper,
    Table,
    TableBody,
    TableContainer,
    TableRow,
    InputLabel,
} from "@mui/material";
// Custom components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftBadge from "components/SoftBadge";
import { formatDate } from "utils/formatters";
import borders from "assets/theme/base/borders";
import colors from "assets/theme/base/colors";
import typography from "assets/theme/base/typography";

const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    maxHeight: "90%",
    width: { xs: "90%", sm: "80%", xxl: "70%" },
    overflow: "auto",
    p: 3,
};

const defaultColumns = [
    { name: "id", desc: "N° Remito", width: "10%", align: "center" },
    { name: "fecha", desc: "Fecha", width: "15%", align: "center" },
    { name: "items", desc: "Items", width: "10%", align: "center" },
    { name: "observaciones", desc: "Observaciones", align: "left" },
    { name: "acciones", desc: "Acciones", width: "10%", align: "center", noOrder: true },
];

const ModalRemitos = ({ open, handleClose, idProvision, provisionData, customColumns, customActions }) => {
    const [remitos, setRemitos] = useState([]);
    const [loading, setLoading] = useState(false);

    // Other
    const { light } = colors;
    const { size, fontWeightBold } = typography;
    const { borderWidth } = borders;

    const [getRemitos] = useLazyQuery(
        gql`
      query getProvision($id: ID!) {
        provisiones(filter: { id: $id }) {
          id
          ingresos {
            id
            tipo
            fechaEntrega
            observaciones
            createdAt
            pagosProveedor {
              id
              filePathFactura
              estado
              observacion
              pagado
            }
            movimientoDetalles {
              id
              cantidad
              sobreEntregaCantidad
              sobreEntregaPorcentaje
              materiaPrima {
                nombre
              }
              idProvisionDetalle
            }
          }
        }
      }
    `,
        {
            onCompleted: (data) => {
                if (data?.provisiones?.length > 0) {
                    const provision = data.provisiones[0];
                    const remitosFiltrados = provision.ingresos.filter(
                        (m) => m.tipo === "Remito Proveedor"
                    );
                    setRemitos(remitosFiltrados);
                } else {
                    setRemitos([]);
                }
                setLoading(false);
            },
            onError: (error) => {
                console.error("Error al cargar remitos:", error);
                setRemitos([]);
                setLoading(false);
            },
            fetchPolicy: "network-only",
        }
    );

    useEffect(() => {
        if (open && idProvision) {
            setLoading(true);
            setRemitos([]);
            getRemitos({ variables: { id: idProvision } });
        }
    }, [open, idProvision, getRemitos]);

    const handleOpenPDF = (idMovimiento) => {
        window.open(`/remitoIndividual/pdf/${idMovimiento}`, "_blank");
    };

    const getTotalProductos = (detalles) => {
        return detalles.length;
    };

    const proveedor = provisionData?.proveedor;
    const columns = customColumns || defaultColumns;
    const actions = customActions;

    const renderColumns = columns.map(({ name, desc, align, width }, key) => {
        let pl;
        let pr;

        if (key === 0) {
            pl = 3;
            pr = 3;
        } else if (key === columns.length - 1) {
            pl = 3;
            pr = 3;
        } else {
            pl = 1;
            pr = 1;
        }

        return (
            <SoftBox
                key={name || key}
                component="th"
                width={width || "auto"}
                pt={1.5}
                pb={1.25}
                pl={align === "left" ? pl : 3}
                pr={align === "right" ? pr : 3}
                textAlign={align}
                fontSize={size.xxs}
                fontWeight={fontWeightBold}
                color="secondary"
                opacity={0.7}
                borderBottom={`${borderWidth[1]} solid ${light.main}`}
            >
                {desc ? desc.toUpperCase() : name ? name.toUpperCase() : `COLUMN ${key + 1}`}
            </SoftBox>
        );
    });

    const renderRemitoRow = (remito) => {
        if (customColumns) {
            // Use custom rendering when custom columns are provided
            return (
                <TableRow key={remito.id}>
                    {columns.map((column, index) => {
                        const columnKey = column.field || column.name || index;
                        
                        if (column.renderCell && column.field !== "acciones") {
                            return (
                                <SoftBox component="td" key={columnKey} textAlign={column.align || "left"} p={1}>
                                    {column.renderCell(remito)}
                                </SoftBox>
                            );
                        } else if ((column.field === "acciones" || column.name === "acciones") && actions) {
                            const remitoActions = typeof actions === 'function' ? actions(remito) : actions;
                            return (
                                <SoftBox component="td" key={columnKey} textAlign="center" p={1}>
                                    <SoftBox display="flex" justifyContent="center" gap={0.5}>
                                        {remitoActions.map((action, actionIndex) => (
                                            <Tooltip key={actionIndex} title={action.tooltip} placement="top">
                                                <IconButton
                                                    size="small"
                                                    color={action.color || "primary"}
                                                    onClick={() => action.onClick(remito)}
                                                >
                                                    <Icon fontSize="small">{action.icon}</Icon>
                                                </IconButton>
                                            </Tooltip>
                                        ))}
                                    </SoftBox>
                                </SoftBox>
                            );
                        }
                        return null;
                    }).filter(Boolean)}
                </TableRow>
            );
        }

        // Default rendering
        return (
            <TableRow key={remito.id}>
                <SoftBox component="td" textAlign="center" p={1}>
                    <SoftBadge
                        variant="gradient"
                        color="info"
                        size="xs"
                        badgeContent={`#${remito.id}`}
                    />
                </SoftBox>
                <SoftBox component="td" textAlign="center" p={1}>
                    <SoftTypography variant="caption" fontWeight="medium">
                        {formatDate(remito.fechaEntrega || remito.createdAt, "dd/MM/yyyy")}
                    </SoftTypography>
                </SoftBox>
                <SoftBox component="td" textAlign="center" p={1}>
                    <SoftTypography variant="caption" fontWeight="medium">
                        {getTotalProductos(remito.movimientoDetalles)}
                    </SoftTypography>
                </SoftBox>
                <SoftBox component="td" p={1}>
                    <SoftTypography 
                        variant="caption" 
                        fontWeight="medium"
                        sx={{
                            maxWidth: "300px",
                            display: "block",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }}
                        title={remito.observaciones}
                    >
                        {remito.observaciones || "-"}
                    </SoftTypography>
                </SoftBox>
                <SoftBox component="td" textAlign="center" p={1}>
                    <Tooltip title="Descargar PDF" placement="top">
                        <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenPDF(remito.id)}
                        >
                            <Icon fontSize="small">picture_as_pdf</Icon>
                        </IconButton>
                    </Tooltip>
                </SoftBox>
            </TableRow>
        );
    };

    return (
        <Modal
            aria-labelledby="modal-remitos-title"
            aria-describedby="modal-remitos-description"
            open={open}
            onClose={handleClose}
            closeAfterTransition
        >
            <Fade in={open}>
                <Card sx={style}>
                    <SoftBox display="flex" flexDirection="column" gap={2}>
                        <Card>
                            <SoftBox p={3} display="flex" flexDirection="column" gap={2}>
                                <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                                    <SoftTypography variant="h6">
                                        Remitos de la OP #{idProvision}
                                    </SoftTypography>
                                    <SoftBox>
                                        <Tooltip title="Cerrar" placement="top">
                                            <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                                                close
                                            </Icon>
                                        </Tooltip>
                                    </SoftBox>
                                </SoftBox>

                                {/* Info del proveedor */}
                                {proveedor && (
                                    <SoftBox display="flex" flexDirection="column" gap={2}>
                                        <SoftBox
                                            display="flex"
                                            justifyContent="space-between"
                                            alignItems={{
                                                xs: "start",
                                                sm: "center",
                                            }}
                                            flexDirection={{
                                                xs: "column",
                                                sm: "row",
                                            }}
                                            gap={2}
                                        >
                                            <SoftBox>
                                                <InputLabel>
                                                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                                                        Proveedor
                                                    </SoftTypography>
                                                </InputLabel>
                                                <SoftTypography variant="h6" color="uproPink" fontWeight="bold">
                                                    {proveedor.razonSocial ||
                                                        `${proveedor.nombre} ${proveedor.apellido}`}
                                                </SoftTypography>
                                            </SoftBox>
                                            <SoftBox>
                                                <InputLabel>
                                                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                                                        CUIT/DNI
                                                    </SoftTypography>
                                                </InputLabel>
                                                <SoftTypography variant="h6" fontWeight="bold">
                                                    {proveedor.documento}
                                                </SoftTypography>
                                            </SoftBox>
                                            <SoftBox>
                                                <InputLabel>
                                                    <SoftTypography component="label" variant="caption" fontWeight="bold">
                                                        Total remitos
                                                    </SoftTypography>
                                                </InputLabel>
                                                <SoftTypography variant="h6" fontWeight="bold">
                                                    {remitos.length}
                                                </SoftTypography>
                                            </SoftBox>
                                        </SoftBox>
                                    </SoftBox>
                                )}
                            </SoftBox>
                        </Card>

                        <Card>
                            <SoftBox
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                p={3}
                                pb={0}
                            >
                                <SoftBox>
                                    <SoftTypography variant="h6">Lista de remitos</SoftTypography>
                                </SoftBox>
                            </SoftBox>

                            <SoftBox
                                sx={{
                                    "& .MuiTableRow-root:not(:last-child)": {
                                        "& td": {
                                            borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                                                `${borderWidth[1]} solid ${borderColor}`,
                                        },
                                    },
                                }}
                            >
                                {loading ? (
                                    <SoftBox display="flex" justifyContent="center" p={4}>
                                        <SoftTypography variant="body2" color="secondary">
                                            Cargando remitos...
                                        </SoftTypography>
                                    </SoftBox>
                                ) : remitos.length === 0 ? (
                                    <SoftBox p={4} textAlign="center">
                                        <Icon color="disabled" fontSize="large" sx={{ mb: 2 }}>
                                            inventory
                                        </Icon>
                                        <SoftTypography variant="body2" color="secondary">
                                            No se han registrado remitos para esta orden.
                                        </SoftTypography>
                                    </SoftBox>
                                ) : (
                                    <TableContainer>
                                        <Table>
                                            <SoftBox component="thead">
                                                <TableRow>{renderColumns}</TableRow>
                                            </SoftBox>
                                            <TableBody>
                                                {remitos.map((remito) => renderRemitoRow(remito))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </SoftBox>
                        </Card>

                        <Card>
                            <SoftBox
                                display="flex"
                                justifyContent="end"
                                alignItems="center"
                                p={2}
                                flexDirection={{
                                    xs: "column",
                                    sm: "row",
                                }}
                                gap={2}
                            >
                                <SoftBox
                                    sx={{
                                        width: {
                                            xs: "100%",
                                            sm: "auto",
                                        },
                                    }}
                                >
                                    <SoftButton color="primary" circular onClick={handleClose} fullWidth>
                                        <Icon sx={{ fontWeight: "light" }}>close</Icon>
                                        &nbsp;Cerrar
                                    </SoftButton>
                                </SoftBox>
                            </SoftBox>
                        </Card>
                    </SoftBox>
                </Card>
            </Fade>
        </Modal>
    );
};

ModalRemitos.propTypes = {
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    idProvision: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    provisionData: PropTypes.object,
    customColumns: PropTypes.array,
    customActions: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
};

export default ModalRemitos;
